"use client"
// DataGridControl.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useConfig } from '@/components/ConfigProvider';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Box,
  IconButton,
  Collapse,
  Typography,
  CircularProgress,
  Checkbox,
  Button,
  TableSortLabel,
  TextField,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem

} from '@mui/material';
import { useTable, Column, usePagination, useResizeColumns, useSortBy, HeaderGroup, useBlockLayout } from 'react-table';
import ControlRenderer, { Control } from '../ControlRenderer';
import { GridPosition, GridControl } from './GridControl';
import { FormModeType, useFormData } from '@/components/FormDataProvider';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { evaluateTemplate, get, isEmpty, isObject, removeEmptyProperties, set } from '../utils/Utils';
import ModalBox, { Action } from '../ModalBox';
import { useValidation, ValidationProvider } from '@/components/ValidationProvider';
import MenuIcon from '@mui/icons-material/ListAlt';
import LeftIcon from '@mui/icons-material/ArrowLeft';
import RightIcon from '@mui/icons-material/ArrowRight';
import EditIcon from '@mui/icons-material/Edit';
import AddControlIcon from '@mui/icons-material/AddCircleOutline';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutline';
import ControlEditor, { getDefaultControl } from '../ControlEditor';
import { getPropertyRule, Rule } from '../utils/Validation';
import RuleEditor, { getDefaultRule } from '../RuleEditor';
import { useAuth } from '@/components/AuthProvider';

/**
 * Represents a data grid control with properties to manage data operations, user interactions, and display settings.
 * Extends the base Control interface.
 */
export interface DataGridControl extends Control {
  /**
   * If true, the data should automatically load if it is not already present.
   * This leverages the absence of data under control's dataKey to trigger loading. (Optional)
   */
  autoload?: boolean;

  /**
   * Specifies the key used for posting data to an API endpoint, 
   * typically formatted as api/form/item/{dataKey}. (Optional)
   */
  postDataKey?: string;

  /**
   * If true, disables pagination controls, allowing all data to be displayed simultaneously. (Optional)
   */
  disablePaging?: boolean;

  /**
   * Sets the selection mode for grid items, allowing either '`single`' or '`multiple`' item selection. (Optional)
   */
  selectionMode?: 'single' | 'multiple';

  /**
   * Determines whether users are allowed to add new items to the data grid. (Optional)
   */
  allowAdd?: boolean;

  /**
   * Determines whether users are allowed to delete items from the data grid. (Optional)
   */
  allowDelete?: boolean;

  /**
   * If true, allows users to search within the data grid. This enhances user experience by filtering visible data. (Optional)
   */
  allowSearch?: boolean;

  /**
   * If true, provides an option to export data from the grid, offering formats like CSV or Excel. (Optional)
   */
  allowExport?: boolean;

  /**
   * If true, hides the data grid entirely when it contains no data, reducing visual clutter. (Optional)
   */
  hideWhenEmpty?: boolean;
  /**
   * If true, the underlying data is an object to be treated as a list of properties. (Optional)
   */
  isPropertyList?: boolean;
  /**
   * Display the totals row in the grid. (Optional)
   */
  showTotals?: boolean;
}

interface HeaderControl extends Control {
  totalsLabel?: string;
  includeInTotals?: boolean;
}

interface Row {
  [key: string]: any;
}

interface DataGridControlProps {
  control: Control;
  data: any;
  onDataChange: (newData: any[]) => void;
  mode: FormModeType;
  onParentMenuClick?: (event: React.MouseEvent<HTMLElement>) => void; // called when a parent menu is clicked
}

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
  onExport: () => void;
  onAdd: () => void;
  onDelete: () => void;
  allowAdd?: boolean;
  allowDelete?: boolean;
  allowExport?: boolean;
  disableDelete: boolean;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange, onExport, onAdd, onDelete, allowExport, allowAdd, allowDelete, disableDelete } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
      {allowExport && (
        <Button onClick={onExport} variant="contained" color="primary" sx={{ mr: 2 }}>
          Export to CSV
        </Button>
      )}
      {allowAdd && (
        <Button onClick={onAdd} variant="contained" color="primary" sx={{ mr: 2 }}>
          Add
        </Button>
      )}
      {allowDelete && (
        <Button onClick={onDelete} variant="contained" color="secondary" disabled={disableDelete} sx={{ mr: 2 }}>
          Delete
        </Button>
      )}
    </Box>
  );
}

const DataGridControl: React.FC<DataGridControlProps> = ({ control, data, onDataChange, mode, onParentMenuClick }) => {
  const { apiUrl } = useConfig(); // Get the API root URL from config
  const { token } = useAuth(); // Get the bearer token
  const [pageIndex, setPageIndex] = useState(0);
  const [tableData, setTableData] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuAnchorElAdd, setMenuAnchorElAdd] = React.useState<null | HTMLElement>(null);
  const [currentControl, setCurrentControl] = React.useState<Control | null>(null);
  const [currentRule, setCurrentRule] = React.useState<Rule | null>(null);
  const [editControl, setEditControl] = React.useState<Control | null>(null);
  const [editRule, setEditRule] = React.useState<Rule | null>(null);
  const [currentColumn, setCurrentColumn] = React.useState<number>(0);
  const [currentRow, setCurrentRow] = React.useState<number>(0);
  const { validateData } = useValidation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { formData } = useFormData();

  const gridControl = control as DataGridControl;
  const disablePaging = gridControl.disablePaging ?? false;
  const allowAdd = mode === 'edit' && (gridControl.allowAdd ?? true);
  const allowExport = gridControl.allowExport ?? false;
  const allowDelete = mode === 'edit' && (gridControl.allowDelete ?? true);
  const allowSearch = gridControl.allowSearch ?? false;
  const showTotals = gridControl.showTotals ?? false;
  let selectionMode = mode === 'edit' ? gridControl.selectionMode : undefined;

  const handleSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCurrentRow(1);
    setMenuAnchorElAdd(event.currentTarget);
  };

  const handleSubmenuClose = () => {
    setCurrentRow(0);
    setMenuAnchorElAdd(null);
  };

  const handleEditRule = (ruleToEdit: Rule) => {
    setEditRule(ruleToEdit);
  };

  const handleEditRuleComplete = () => {
    if (editRule) {
      // need to remove empty props to validate
      removeEmptyProperties(editRule);
    }
    setEditRule(null);
    validateData();
  };

  const handleAddRule = (control: Control) => {
    if (validationRule.properties && control.dataKey) {
      var newRule = getDefaultRule(control.type);
      if (newRule) {
        validationRule.properties[control.dataKey] = newRule;
        setEditRule(newRule);
      }
    }
  };

  const handleDeleteRule = (control: Control) => {
    if (validationRule.properties && control.dataKey) {
      delete validationRule.properties[control.dataKey];
    }
    setEditRule(null);
    validateData();
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setCurrentControl(null);
  };

  const handleEditControl = (controlToEdit: Control) => {
    setEditControl(controlToEdit);
  };

  const handleEditComplete = (controlToEdit: Control) => {
    // copy the label to the header
    const colIndex = controlToEdit.gridPosition?.column;
    const rowIndex = controlToEdit.gridPosition?.row;
    if (colIndex !== undefined && rowIndex === 1 && controlToEdit.label && controlToEdit.label !== '') {
      const c = getControlAtPosition(0, colIndex);
      if (c) {
        c.label = controlToEdit.label;
      }
    }
    setEditControl(null);
  };

  const handleEditHeaderControl = (controlToEdit: Control) => {
    const colIndex = controlToEdit.gridPosition?.column;
    if (colIndex !== undefined) {
      const c = getControlAtPosition(0, colIndex);
      if (c) {
        c.type = "header"
        setEditControl(c);
      }
    }
  };

  const handleAddControl = (type: string) => {
    var newControl = getDefaultControl(type);
    const colNum = columns.length;
    if (currentRow === 2) {
      let c = getControlAtPosition(currentRow, 0);
      if (c) {
        c.controls?.push(newControl);
      } else {
        // add to row details
        newControl.gridPosition = {
          row: currentRow,
          column: 0
        };
        control.controls?.push(newControl);
      }
    } else if (currentRow === 1) {
      // add column
      var newControls = [];
      newControl.gridPosition = {
        row: currentRow,
        column: currentColumn
      };
      // relocate the controls
      if (control.controls && control.controls.length > 0) {
        for (let i = 0; i < control.controls.length; i++) {
          const controlItem = control.controls[i];
          if (controlItem.gridPosition && (controlItem.gridPosition?.column >= currentColumn)) {
            if (controlItem.gridPosition?.column == currentColumn) {
              if (controlItem.gridPosition?.row == 0) {
                var newHeaderControl = getDefaultControl("header");
                newHeaderControl.label = `Column ${currentColumn}`;
                newHeaderControl.gridPosition = {
                  row: 0,
                  column: currentColumn
                };
                newControls.push(newHeaderControl);
              } else if (controlItem.gridPosition?.row == 1) {
                newControls.push(newControl);
              }
            }
            ++controlItem.gridPosition.column;
          }
          newControls.push(controlItem);
          if (colNum == currentColumn && controlItem.gridPosition?.column == currentColumn - 1) {
            // adding to the end of the grid
            if (controlItem.gridPosition?.row == 0) {
              var newHeaderControl = getDefaultControl("header");
              newHeaderControl.label = `Column ${currentColumn}`;
              newHeaderControl.gridPosition = {
                row: 0,
                column: currentColumn
              };
              newControls.push(newHeaderControl);
            } else if (controlItem.gridPosition?.row == 1) {
              newControls.push(newControl);
            }
          }
        }
      } else {
        var newHeaderControl = getDefaultControl("header");
        newHeaderControl.label = `Column ${currentColumn}`;
        newHeaderControl.gridPosition = {
          row: 0,
          column: currentColumn
        };
        newControls.push(newHeaderControl);
        newControls.push(newControl);
      }

      // Update the grid control's controls
      set(control, 'controls', newControls);
    }

    setEditControl(newControl);
    setMenuAnchorEl(null);
    setMenuAnchorElAdd(null);
  };

  const handleAddRowDetailsControl = (event: React.MouseEvent<HTMLElement>) => {
    setCurrentRow(2);
    setMenuAnchorElAdd(event.currentTarget);
  };

  const handleDeleteRowDetails = () => {
    // adduming the last element
    control.controls?.pop();
  };

  const handleInsertColumn = (event: React.MouseEvent<HTMLElement>, toRight: boolean) => {
    setCurrentRow(1);
    if (toRight) {
      setCurrentColumn(currentColumn + 1);
    }
    setMenuAnchorElAdd(event.currentTarget);
  };

  const handleDeleteColumn = (currentColumn: number) => {
    // Remove the control from the grid's controls
    const updatedControls = gridControl.controls?.filter((c: Control) => c.gridPosition?.column !== currentColumn);

    // reindex controls
    if (control.controls) {
      for (let i = 0; i < control.controls.length; i++) {
        const controlItem = control.controls[i];
        if (controlItem.gridPosition && controlItem.gridPosition?.column > currentColumn) {
          --controlItem.gridPosition.column;
        }
      }
    }

    // Update the grid control's controls
    set(control, 'controls', updatedControls);
  };

  if (allowDelete && !selectionMode) {
    // delete requires row selection
    selectionMode = 'single';
  }

  useEffect(() => {
    const newExpandedRows: { [key: number]: boolean } = {};
    tableData.forEach((_: any, index: number) => {
      newExpandedRows[index] = allExpanded;
    });
    setExpandedRows(newExpandedRows);
  }, [allExpanded, tableData]);

  const toggleExpandRow = (rowIndex: number) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowIndex]: !prev[rowIndex]
    }));
  };

  const toggleExpandAll = () => {
    setAllExpanded(prev => !prev);
  };

  const toggleRowSelected = (rowIndex: number, selectionMode?: 'single' | 'multiple') => {
    if (!selectionMode) {
      return;
    }
    setSelectedRows(prev => {
      const newSelectedRows = new Set(prev);
      if (newSelectedRows.has(rowIndex)) {
        if (selectionMode === 'multiple') {
          newSelectedRows.delete(rowIndex);
        }
      } else {
        if (selectionMode === 'single') {
          newSelectedRows.clear();
        }
        newSelectedRows.add(rowIndex);
      }
      return newSelectedRows;
    });
  };

  const getControlAtPosition = React.useCallback((rowIndex: number, columnIndex: number): Control | undefined => {
    const position: GridPosition = {
      row: rowIndex,
      column: columnIndex,
    };

    return (control.controls || []).find((c: Control) => {
      const cPosition: GridPosition = c.gridPosition || { row: 0, column: 0 };
      return (
        cPosition.row === position.row &&
        cPosition.column === position.column
      );
    });
  }, [control.controls]);

  const detailsControl = getControlAtPosition(2, 0);
  const validationRule = useMemo(
    () => control.dataContextKey && control.dataKey ? get(get(formData, "Validation"), control.dataKey, {}) : undefined,
    [control.dataContextKey, control.dataKey, formData]
  );

  const handleMenuOpen = React.useCallback((event: React.MouseEvent<HTMLElement>, columnIndex: number) => {
    event.stopPropagation();
    let c = columnIndex >= 0 ? getControlAtPosition(1, columnIndex) : control;
    if (c) {
      setCurrentControl(c);
      const currentRule = c.dataKey && getPropertyRule(validationRule, c.dataKey);
      if (currentRule) {
        setCurrentRule(currentRule);
      }
      else {
        setCurrentRule(null);
      }
    }
    setCurrentColumn(columnIndex);
    setMenuAnchorEl(event.currentTarget);
  }, [getControlAtPosition, control, validationRule]);

  const fetchData = React.useCallback(async (dataKey: string, postDataKey: string) => {
    setLoading(true);
    setError(null);

    try {
      let postData = postDataKey ? get(data, postDataKey, undefined) : {};
      //console.log(data)
      //console.log(postDataKey)
      if (apiUrl === '') {
        // the api url has not yet been retrieved
        return;
      }

      if (!postData) {
        throw 'Invalid post data';
      }
      if (typeof (postData) !== 'object') {
        postData = { [postDataKey]: postData };
      }

      const response = await fetch(evaluateTemplate(`${apiUrl}/Form/item/${dataKey}`, data, formData), {
        method: 'POST',
        mode: 'cors', // Allow cross-origin requests
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
          // Add any additional headers if needed
        },
        body: JSON.stringify(postData)
      });

      if (response.ok) {
        const result = await response.json();
        if (Array.isArray(result)) {
          set(data, dataKey, result);
          setTableData(result);
        }
      } else {
        const errorText = await response.text();
        setError(`Failed to fetch form data item: ${dataKey} ${response.status} ${errorText}`);
      }
    } catch (err) {
      console.error('Error fetching grid data:', err);
      setError('Error fetching grid data');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, data, formData, token]);

  useEffect(() => {
    //console.log(data);
    //console.log(gridControl.dataKey);
    const isPropertyList = gridControl.isPropertyList ?? false;
    var tableData = get(data, gridControl.dataKey ?? '', undefined);
    if (Array.isArray(tableData)) {
      if (tableData.length == 0 && gridControl.autoload) {
        if (apiUrl && token) {
          fetchData(gridControl.dataKey as string, gridControl.postDataKey as string);
        } else {
          // wait for authentication
          setLoading(true);
        }
      }
      else {
        setTableData(tableData);
      }
    }
    else if (isObject(tableData) && isPropertyList) {
      setTableData(Object.entries(tableData).map(([key, value]) => ({
        key: key,
        value: value,
      })));
    }
    else if (gridControl.autoload) {
      fetchData(gridControl.dataKey as string, gridControl.postDataKey as string);
    }
    else if (gridControl.dataKey) {
      tableData = [];
      set(data, gridControl.dataKey, tableData);
      setTableData(tableData);
      console.log(`creating a missing datacontext ${gridControl.dataKey}`);
    }
  }, [
    gridControl.autoload,
    gridControl.dataKey,
    gridControl.postDataKey,
    gridControl.isPropertyList,
    data,
    apiUrl,
    token,
    fetchData
  ]);

  // Extract columns from control.controls
  const columns: readonly Column<Row>[] = useMemo(
    () => control.controls
      ?.filter(columnControl => columnControl.gridPosition?.row === 0 && (!isMobile || columnControl.gridPosition?.showInMobile !== false))
      .map((columnControl, index) => {
        const cellControl = getControlAtPosition(1, columnControl.gridPosition?.column ?? index);
        return {
          id: 'id' + columnControl.gridPosition?.column,
          Header: () => (
            <div style={{ position: 'relative' }}>
              <span>{columnControl.label ?? ''}</span>
              {mode === 'design' && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    display: 'flex'
                  }}
                >
                  <IconButton
                    style={{
                      padding: 0,
                      color: cellControl && cellControl.dataKey && getPropertyRule(validationRule, cellControl.dataKey) ? 'purple' : 'gray',
                      zIndex: 1000
                    }}
                    onClick={(event) => handleMenuOpen(event, columnControl.gridPosition?.column ?? index)} // pass the current column control
                  >
                    <MenuIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
            </div>
          ),
          canResize: true,
          Cell: ({ row }) => {
            // Define the parent context in case if it required
            if (!row.original.hasOwnProperty('parent')) {
              Object.defineProperty(row.original, 'parent', { get: () => data, configurable: true });
            }
            // Render each cell using ControlRenderer
            return <ControlRenderer control={cellControl ?? columnControl} data={row.original} onDataChange={onDataChange} mode={mode} />;
          },
          accessor: cellControl?.dataKey ?? '', // Add accessor for sorting
        };
      }) ?? [],
    [
      control.controls,
      onDataChange,
      mode,
      isMobile,
      data,
      getControlAtPosition,
      handleMenuOpen,
      validationRule
    ]);

  //console.log(tableData);

  const filterData = (data: any[], query: string) => {
    if (!query) return data;
    return data.filter(row =>
      Object.values(row).some(
        value => String(value).toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  const filteredData = useMemo(() => filterData(tableData, searchQuery), [tableData, searchQuery]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page, // Use `page` for rows
    prepareRow,
    setPageSize: setTablePageSize,
    gotoPage,
    rows,
  } = useTable(
    { columns, data: filteredData, initialState: { pageIndex, pageSize } },
    useSortBy,
    usePagination,
    //useBlockLayout,
    useResizeColumns
  );

  const prepareValueForCSV = (val: string) => {
    if (val.startsWith("<a href='http")) {
      var s = val.split(" ");
      var string = s[1];
      var indexStart = string.indexOf("http")
      var link = string.slice(indexStart, (string.length) - 1)

      val = "=HYPERLINK(\"weblink\", \"View\")"
      val = val.replace("weblink", link)
    };

    val = '' + val;
    // Escape quotes to avoid ending the value prematurely.
    val = val.replace(/"/g, '""');
    //fix % signs in strings for csv
    val = val.replace(/%(?!\d+)/g, '%25');
    return '"' + val + '"';
  };

  const handleExport = () => {
    const csvRows = [];
    // Get the headers (keys of the first object)
    const headers = Object.keys(tableData[0]);
    csvRows.push(headers.join(',')); // Join headers with comma
    // Loop through the array and convert each object to a CSV row
    for (const obj of tableData) {
      const values = headers.map(header => {
        const val = obj[header];
        return typeof val === 'string' ? prepareValueForCSV(val) : val;
      });
      csvRows.push(values.join(',')); // Join values with comma

      // Step 2: Create a Blob from the CSV string
      const csvString = csvRows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv' });

      // Step 3: Create a download link and click it
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', 'data.csv');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a); // Clean up
    }
  };

  const handleAddRow = () => {
    const newRow: Row = {};
    control.controls?.forEach(columnControl => {
      const { dataKey } = columnControl;
      if (dataKey) {
        newRow[dataKey] = null;
      }
    });
    const updatedTableData = [...tableData, newRow];
    if (control.dataKey) {
      set(data, control.dataKey, updatedTableData);
    }
    setTableData(updatedTableData);
    onDataChange(updatedTableData);
  };

  const handleDeleteRows = () => {
    const updatedTableData = tableData.filter((_, index) => !selectedRows.has(index));
    if (control.dataKey) {
      set(data, control.dataKey, updatedTableData);
    }
    setTableData(updatedTableData);
    setSelectedRows(new Set());
    onDataChange(updatedTableData);
  };

  const withValidation = (ControlComponent: React.JSX.Element) => {
    return (
      mode === "edit" || mode === "design" ? (
        <ValidationProvider rule={validationRule}>
          {ControlComponent}
        </ValidationProvider>
      ) : (
        ControlComponent
      )
    );
  };

  // Calculate totals function
  const calculateTotals = (data: Row[]): (string | number | null)[] => {
    return columns.map((column, index) => {
      const headerControl = getControlAtPosition(0, index) as HeaderControl;
      if (headerControl) {
        if (headerControl.totalsLabel) {
          return headerControl.totalsLabel;
        }
        else if (headerControl.includeInTotals && data.length > 0 && column.accessor) {
          const accessor = column.accessor.toString(); // Accessor as a string
          if (typeof data[0][accessor] === 'number') {
            return data.reduce((sum, row) => sum + (row[accessor] || 0), 0);
          }
        }
      }

      return null; // Return null for non-numeric columns
    });
  };

  if (gridControl.hideWhenEmpty && isEmpty(tableData)) {
    return;
  }

  return (
    <div className={`eform-datagrid eform-datagrid-${theme.palette.mode}`} style={{ position: 'relative' }}>
      {control.label && (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
          <Typography className="eform-datagrid-label">{control.label}</Typography>
        </Box>
      )}
      {loading &&
        <>
          <CircularProgress color="primary" />
          <Typography color="textSecondary">Loading...</Typography>
        </>}
      {error && <Typography color="error">{error}</Typography>}
      {!loading && !error && (
        <>
          {allowSearch && (
            <TextField
              label="Search"
              variant="outlined"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              fullWidth
              margin="normal"
            />
          )}
          <Table {...getTableProps()}>
            <TableHead>
              {headerGroups.map(headerGroup => {
                const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps()
                return (
                  <TableRow key={key} {...restHeaderGroupProps}>
                    {detailsControl && <TableCell>
                      <IconButton onClick={toggleExpandAll}>
                        {allExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>}
                    {headerGroup.headers.map(column => {
                      const { key, ...restColumn } = column.getHeaderProps(column.getSortByToggleProps())
                      return (
                        <TableCell key={key} {...restColumn}>
                          {column.render('Header')}
                          <TableSortLabel
                            active={column.isSorted}
                            direction={column.isSortedDesc ? 'desc' : 'asc'}
                          />
                          <div
                            {...column.getResizerProps()}
                            className={`resizer${column.isResizing ? " isResizing" : ""}`}
                            onMouseDown={e => {
                              e.stopPropagation();
                            }}
                          />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableHead>
            <TableBody {...getTableBodyProps()}>
              {(disablePaging ? rows : page).map((row) => {
                prepareRow(row);
                const { key, ...restRowProps } = row.getRowProps();
                const isSelected = selectedRows.has(row.index);
                return withValidation(
                  <React.Fragment key={key}>
                    <TableRow key={key} {...restRowProps} selected={isSelected} onClick={() => toggleRowSelected(row.index, selectionMode)}>
                      {detailsControl && <TableCell>
                        <IconButton className='eform-gridbutton' onClick={(event) => {
                          event.stopPropagation();
                          toggleExpandRow(row.index)
                        }}>
                          {expandedRows[row.index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>}
                      {row.cells.map((cell) => {
                        const { key, ...restCellProps } = cell.getCellProps();
                        return (
                          <TableCell key={key} {...restCellProps}>
                            {cell.render('Cell')}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {detailsControl && <TableRow>
                      <TableCell colSpan={columns.length + 1} style={{ paddingBottom: 0, paddingTop: 0 }}>
                        <Collapse in={expandedRows[row.index]} timeout="auto" unmountOnExit>
                          <Box margin={1} display="flex" justifyContent="center" alignItems="center">
                            {/* Render details content here */}
                            <ControlRenderer
                              control={detailsControl}
                              data={row.original}
                              onDataChange={onDataChange}
                              mode={mode}
                              onParentMenuClick={(event) => { setCurrentControl(detailsControl); setMenuAnchorEl(event.currentTarget); }} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>}
                  </React.Fragment>
                );
              })}
              {showTotals && (
                <TableRow>
                  {detailsControl && <TableCell />}
                  {calculateTotals(tableData).map((total, index) => (
                    <TableCell key={index} style={{ fontWeight: 'bold' }}>{total !== null ? total : ''}</TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!disablePaging && (
            <TablePagination
              component="div"
              count={filteredData.length} // Total number of rows in the data set
              page={pageIndex}
              onPageChange={(event, newPage) => {
                setAllExpanded(false);
                setExpandedRows({});
                setPageIndex(newPage);
                gotoPage(newPage);
              }}
              rowsPerPage={pageSize}
              onRowsPerPageChange={event => {
                const newPageSize = Number(event.target.value);
                setAllExpanded(false);
                setExpandedRows({});
                setTablePageSize(newPageSize);
                setPageSize(newPageSize);
                setPageIndex(0);
              }}
              rowsPerPageOptions={[10, 20, 30]} // optional: configure rows per page options
              // Custom controls
              ActionsComponent={(props) => (
                <TablePaginationActions
                  {...props}
                  onAdd={handleAddRow}
                  onExport={handleExport}
                  onDelete={() => setConfirmDelete(true)}
                  allowExport={allowExport}
                  allowAdd={allowAdd}
                  allowDelete={allowDelete}
                  disableDelete={selectedRows.size === 0}
                />
              )}
            />
          )}
          {confirmDelete && (
            <ModalBox
              action={{
                type: "messagebox",
                payload: {
                  message: `Do you want to delete the selected record${selectedRows.size > 1 ? "s" : ""}?`,
                  okbutton: true,
                  cancelbutton: true
                }
              }}
              data={data}
              onDataChange={() => { }}
              mode="edit"
              onComplete={() => {
                setConfirmDelete(false);
                handleDeleteRows();
              }}
              onError={() => {
                setConfirmDelete(false);
              }}
              onClose={() => {
                setConfirmDelete(false);
              }} />
          )}
        </>
      )}
      {mode === 'design' && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'flex'
          }}
        >
          <IconButton
            style={{
              padding: 0,
              color: 'gray',
              zIndex: 1000
            }}
            onClick={(event) => handleMenuOpen(event, -1)}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        </div>
      )}
      {menuAnchorEl && (<Menu
        id="control-menu"
        disableScrollLock
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {onParentMenuClick &&
          <MenuItem onClick={(event) => { onParentMenuClick(event); }}>
            <MenuIcon className="eform-menu-icon" />
            <Typography variant="inherit">Parent Control</Typography>
          </MenuItem>
        }
        {currentControl && currentColumn >= 0 && <>
          <MenuItem onClick={() => { handleEditControl(currentControl); handleMenuClose(); }}>
            <EditIcon className="eform-menu-icon" style={{ color: "green" }} />
            <Typography variant="inherit">{`Edit Column (${currentControl.type}) Properties`}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleEditHeaderControl(currentControl); handleMenuClose(); }}>
            <EditIcon className="eform-menu-icon" style={{ color: "green" }} />
            <Typography variant="inherit">{`Edit Column Header`}</Typography>
          </MenuItem>
        </>
        }
        {currentControl && currentColumn < 0 && <>
          <MenuItem onClick={() => { handleEditControl(currentControl); handleMenuClose(); }}>
            <EditIcon className="eform-menu-icon" style={{ color: "green" }} />
            <Typography variant="inherit">{`Edit ${currentControl.type} Properties`}</Typography>
          </MenuItem>
        </>
        }
        {currentRule && validationRule?.properties && currentControl?.dataKey && <>
          <MenuItem onClick={() => { handleEditRule(currentRule); handleMenuClose(); }}>
            <EditIcon className="eform-menu-icon" style={{ color: "purple" }} />
            <Typography variant="inherit">Edit Validation</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleDeleteRule(currentControl); handleMenuClose(); }}>
            <RemoveIcon className="eform-menu-icon" style={{ color: "purple" }} />
            <Typography variant="inherit">Delete Validation</Typography>
          </MenuItem>
        </>}
        {!currentRule && validationRule?.properties && currentControl?.dataKey && <>
          <MenuItem onClick={() => { handleAddRule(currentControl); handleMenuClose(); }}>
            <AddControlIcon className="eform-menu-icon" style={{ color: "purple" }} />
            <Typography variant="inherit">Add Validation</Typography>
          </MenuItem>
        </>}
        {detailsControl && <>
          <MenuItem onClick={() => { handleEditControl(detailsControl); handleMenuClose(); }}>
            <EditIcon className="eform-menu-icon" style={{ color: "blue" }} />
            <Typography variant="inherit">Edit Row Details</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleDeleteRowDetails(); handleMenuClose(); }}>
            <RemoveIcon className="eform-menu-icon" style={{ color: "blue" }} />
            <Typography variant="inherit">Delete Row Details</Typography>
          </MenuItem>
        </>
        }
        {detailsControl && (detailsControl.type === "tab" || detailsControl.type === "flex") &&
          <MenuItem onClick={handleAddRowDetailsControl}>
            <AddControlIcon className="eform-menu-icon" style={{ color: "blue" }} />
            <Typography variant="inherit">{`Add Item to row details (${detailsControl.type}) Control`}</Typography>
          </MenuItem>
        }
        {!detailsControl &&
          <MenuItem onClick={handleAddRowDetailsControl}>
            <AddControlIcon className="eform-menu-icon" style={{ color: "blue" }} />
            <Typography variant="inherit">Add Row Details</Typography>
          </MenuItem>
        }
        {(!currentControl || currentControl.type === "tab" || currentControl.type === "flex") &&
          <MenuItem onClick={handleSubmenuOpen}>
            <AddControlIcon className="eform-menu-icon" style={{ color: "green" }} />
            <Typography variant="inherit">{currentControl ? `Add Item to ${currentControl.type} Control` : "Add Control"}</Typography>
          </MenuItem>
        }
        {menuAnchorElAdd &&
          <Menu
            id="addmenu"
            anchorEl={menuAnchorElAdd}
            open={Boolean(menuAnchorElAdd)}
            onClose={handleSubmenuClose}
          >
            <MenuItem onClick={() => { handleAddControl("label") }}>
              <Typography variant="inherit">Label</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("text") }}>
              <Typography variant="inherit">Text</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("textarea") }}>
              <Typography variant="inherit">TextArea</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("select") }}>
              <Typography variant="inherit">Dropdown</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("date") }}>
              <Typography variant="inherit">DatePicker</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("check") }}>
              <Typography variant="inherit">Checkbox</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("gridbutton") }}>
              <Typography variant="inherit">Button</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("grid") }}>
              <Typography variant="inherit">Grid</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("flex") }}>
              <Typography variant="inherit">FlexGrid</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("datagrid") }}>
              <Typography variant="inherit">DataGrid</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("tab") }}>
              <Typography variant="inherit">Tab Control</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("upload") }}>
              <Typography variant="inherit">Upload Control</Typography>
            </MenuItem>
          </Menu>
        }
        {currentColumn >= 0 && <MenuItem onClick={(event) => { handleInsertColumn(event, false) }}>
          <LeftIcon className="eform-menu-icon" />
          <Typography variant="inherit">Insert Column Left</Typography>
        </MenuItem>}
        <MenuItem onClick={(event) => { handleInsertColumn(event, true) }}>
          <RightIcon className="eform-menu-icon" />
          <Typography variant="inherit">Insert Column Right</Typography>
        </MenuItem>
        {currentColumn >= 0 && <MenuItem onClick={() => { handleDeleteColumn(currentColumn); handleMenuClose(); }}>
          <RemoveIcon className="eform-menu-icon" />
          <Typography variant="inherit">Delete Column</Typography>
        </MenuItem>}
      </Menu>)
      }
      {editControl &&
        <ControlEditor
          controlToEdit={editControl}
          data={tableData}
          dataKey={gridControl.dataKey}
          onComplete={() => handleEditComplete(editControl)}
          onError={() => setEditControl(null)} />}
      {editRule &&
        <RuleEditor
          ruleToEdit={editRule}
          data={tableData}
          dataKey={gridControl.dataKey}
          onComplete={handleEditRuleComplete}
          onError={() => setEditRule(null)} />}
    </div>
  );
};

export default DataGridControl;
